import { DateTime } from 'luxon';
import Shell from "../../../../../components/specific/Shell.tsx";
import {User} from "../../../../../api/accessControl/User.ts";
import {useEffect, useMemo, useRef, useState} from "react";
import {BackendApi} from "../../../../../api/api.ts";
import {fetchDocumentContentBlob} from "../../../../../api/fetchDocumentContent.ts";
import {BusinessReconciliationOverview, CreditCardStatementDocumentOption, MissingDocumentRequest, RenderTypeEnum, SortOptionsEnum} from "./types.ts";
import {getYesterdayDate} from "../../../../../lib/TFICLib.ts";
import {documentConflictMessage} from "../../../../../lib/apiErrors.ts";
import {QuarterYear} from "../../../../../components/specific/QuarterYearSelector.tsx";
import {Client} from "../../Client.ts";
import UnmatchedTransactionsFilterBar from "./UnmatchedTransactionsFilterBar.tsx";
import {CashFlowDirection} from "./BusinessCardFilter.tsx";
import {
    DEFAULT_SORT_OPTION,
    filterDocumentsByDirection,
    filterTransactionsByDirection,
    sortItemsByOption,
} from "./unmatchedTransactionsUtils.ts";
import DndMatchPage from "./DndMatchPage.tsx";
import {Switch} from "@headlessui/react";
import {CreateBlacklistRuleDrawer} from "../partnerCreateBlacklistRule/CreateBlacklistRuleDrawer.tsx";
import {CreditCardSettlementDrawer} from "./components/creditCardSettlementDrawer/CreditCardSettlementDrawer.tsx";
import {
    buildSettlementSubmitPayload,
    mapCreditCardStatements,
    type CreditCardSettlementFormPayload,
} from "./components/creditCardSettlementDrawer/utils/creditCardSettlementUtils.ts";
import {
    BankAccountData,
    BankAccountType,
    CustomerBankAccount,
} from "../../../../../api/customerBankAccounts/CustomerBankAccount.ts";
import type {TFICDocument} from "../../../../../views/DocumentDrawer/types/Document";
import {Rule} from "../partnerCreateBlacklistRule/types.ts";
import {buildInitialBlacklistRuleState} from "../partnerCreateBlacklistRule/logic.ts";
import RelativeLoadingOverlayFrame from "../../../../../components/RelativeLoadingOverlayFrame.tsx";
import {Transaction as DndTransaction} from "./dndTypes.ts";
import {DEFAULT_GL_ACCOUNT, GlAccount, useGLAccounts} from "../../../../../hooks/useGLAccounts.ts";
import {SubledgerProvider} from "../../../../../contexts/SubledgerContext.tsx";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import { BookTransactionModal, TransactionToBook } from "../../../../../components/specific/BookTransactionModal/BookTransactionModal.tsx";
import { BookAwayModal, TransactionToBookAway, BookAwayPayload } from "./components/BookAwayModal.tsx";
import { BookDifferenceModal, BookDifferenceValues } from "./components/BookDifferenceModal.tsx";
import { useFilterStore } from "../../../../../contexts/FilterStore.tsx";
import { usePartnerClientScope } from "../../../../../hooks/usePartnerClientScope.ts";
import {ClientSubtitle} from "../../../../../components/specific/ClientSwitcher";
import {PartnerClientToolbar, getVisibleGoToItemIds} from "../../../../../components/specific/PartnerClientToolbar";
import {extractApiErrorMessage} from "../../../../../lib/apiErrors.ts";
import {useReloadOnDocumentDrawerChange} from "./hooks/useReloadOnDocumentDrawerChange.ts";
import type {DocumentDirection, VatCode} from '../../../../../generated/enums.ts';

export default function PartnerUnmatchedTransactionsPage({currentUser}: { currentUser: User }) {
    const api = new BackendApi(currentUser.getToken());
    const { glAccounts: glAccounts, isLoading: glAccountsLoading } = useGLAccounts(currentUser);
    const { dispatch } = useNotifications();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [businessReconciliationOverviews, setBusinessReconciliationOverviews] = useState<BusinessReconciliationOverview[]>([]);

    const clientScope = usePartnerClientScope(currentUser);
    const clients = clientScope.clients;
    const [showMatched, setShowMatched] = useState<boolean>(false);

    const [selectedFilter, setSelectedFilter] = useState<CashFlowDirection>('all');
    const [selectedSort, setSelectedSort] = useState<SortOptionsEnum>(DEFAULT_SORT_OPTION);

    const [isCreateBlacklistRuleDrawerOpen, setIsCreateBlacklistRuleDrawerOpen] = useState<boolean>(false);
    const [blacklistRuleDrawerInitialValues, setBlacklistRuleDrawerInitialValues] = useState<{
        businessId: string | null;
        relationName: string | null;
        relationIban: string | null;
        amount: number | null;
        description: string | null;
        generalLedgerAccountId: string | null;
    }>({
        businessId: null,
        relationName: null,
        relationIban: null,
        amount: null,
        description: null,
        generalLedgerAccountId: null,
    });
    const [blacklistRuleDrawerSelectedBusiness, setBlacklistRuleDrawerSelectedBusiness] = useState<Client | null>(null);

    const [transactionToBook, setTransactionToBook] = useState<TransactionToBook | null>(null);
    const [transactionToBookAway, setTransactionToBookAway] = useState<TransactionToBookAway | null>(null);
    const [isRecategorizationModalLoading, setIsRecategorizationModalLoading] = useState<boolean>(false);

    const canBookVatOnTransaction = currentUser.flags.getFlag('PARTNER__CAN_BOOK_VAT_ON_TRANSACTION');

    const [bookDifferenceMatchId, setBookDifferenceMatchId] = useState<string | null>(null);
    const [bookDifferenceDefaultDate, setBookDifferenceDefaultDate] = useState<string>("");
    const [isBookDifferenceSubmitting, setIsBookDifferenceSubmitting] = useState<boolean>(false);

    const [isCreditCardSettlementDrawerOpen, setIsCreditCardSettlementDrawerOpen] = useState<boolean>(false);
    const [creditCardSettlementTransaction, setCreditCardSettlementTransaction] = useState<MissingDocumentRequest | null>(null);
    const [creditCardSettlementBusinessId, setCreditCardSettlementBusinessId] = useState<string | null>(null);
    const [creditCardSettlementDocuments, setCreditCardSettlementDocuments] = useState<CreditCardStatementDocumentOption[]>([]);
    const [creditCardSettlementAccounts, setCreditCardSettlementAccounts] = useState<CustomerBankAccount[]>([]);
    const [creditCardSettlementPreviewDocument, setCreditCardSettlementPreviewDocument] = useState<TFICDocument | null>(null);
    const [isCreditCardSettlementLoading, setIsCreditCardSettlementLoading] = useState<boolean>(false);
    const [isCreditCardSettlementPreviewLoading, setIsCreditCardSettlementPreviewLoading] = useState<boolean>(false);
    const [isCreditCardSettlementSubmitting, setIsCreditCardSettlementSubmitting] = useState<boolean>(false);

    const { state } = useFilterStore();
    const startDate = state.common.dateRange.from;
    const endDate = state.common.dateRange.to;

    const quarterYear = useMemo(() => {
        const dt = DateTime.fromISO(startDate);
        return {
            year: dt.isValid ? dt.year : DateTime.now().year,
            quarter: dt.isValid ? dt.quarter : DateTime.now().quarter
        };
    }, [startDate]);


    function changeShowMatched(isOpen: boolean) {
        setShowMatched(isOpen);
        localStorage.setItem('showMatchedTransactionsAndDocuments', isOpen.toString());
    }

    async function onLoad(fromDate: string, qy: QuarterYear, clientId: string | null = null, toDate?: string) {
        try {
            const questionItemsResponse = await api.Partner__getBusinessReconciliationOverviews(
                fromDate, clientId, toDate ?? null,
            );

            setBusinessReconciliationOverviews(questionItemsResponse as BusinessReconciliationOverview[]);
        } catch (error) {
            console.error("Error loading data:", error);
        }
    }

    async function onFilterChange(
        fromDate: string,
        qy: QuarterYear,
        clientId: string | null = null,
        toDate?: string
    ) {
        setIsLoading(true);
        await onLoad(fromDate, qy, clientId, toDate);
        setIsLoading(false);

    }


    const handleFilterChange = (direction: CashFlowDirection) => {
        setSelectedFilter(direction);
    };

    const handleSortChange = (sortOption: SortOptionsEnum) => {
        setSelectedSort(sortOption);
    };

    const handleAccept = async (
        transactionIds: string[],
        billIds: string[]
    ) => {
        try {

            await api.Reconciliation__manuallyMatch({
                bill_ids: billIds, transaction_ids: transactionIds
            })

            await onLoad(startDate, quarterYear, clientScope.selectedClientId, endDate);
        } catch (error) {
            alert('Er is iets misgegaan tijdens het matchen')
        }
    };

    const handleManualAddPartsMatch = async (
        matchId: string,
        transactionIds: string[],
        billIds: string[]
    ) => {
        try {
            await api.Reconciliation__manuallyAddPartsMatch({
                match_id: matchId,
                transaction_ids: transactionIds,
                bill_ids: billIds
            });
            await onLoad(startDate, quarterYear, clientScope.selectedClientId, endDate);
        } catch (error) {
            alert('Er is iets misgegaan tijdens het toevoegen van onderdelen aan de match');
        }
    };

    const onBookTransactionAsPrivate = async (transactionId: string) => {
        try {

            await api.Transactions__bookAsPrivate({transaction_id: transactionId});

            await onLoad(startDate, quarterYear, clientScope.selectedClientId, endDate);
        } catch (error) {
            alert('Er is iets misgegaan tijdens het markeren als privé');
            throw error;
        }
    };

    const onMarkBillAsPrivatelyPaid = async (documentId: string) => {
        try {
            await api.Partner__document__bill__markAsPrivatelyPaid({document_id: documentId});
            await onLoad(startDate, quarterYear, clientScope.selectedClientId, endDate);
        } catch (error) {
            alert(documentConflictMessage(error) ?? 'Er is iets misgegaan tijdens het markeren als niet-matchbaar');
            throw error;
        }
    };

    const handleUnmatch = async (billIds: string[], matchId: string, transactionIds: string[]) => {
        try {
            await api.Reconciliation__manuallyUnmatch({
                bill_ids: billIds,
                match_id: matchId,
                transaction_ids: transactionIds
            });
            await onLoad(startDate, quarterYear, clientScope.selectedClientId, endDate);
        } catch (error) {
            alert('Er is iets misgegaan tijdens het ongedaan maken van de match');
        }
    };

    const handleMarkAsComplete = (matchId: string, defaultDate: string) => {
        setBookDifferenceMatchId(matchId);
        setBookDifferenceDefaultDate(defaultDate);
    };

    const handleConfirmBookDifference = async (values: BookDifferenceValues) => {
        if (!bookDifferenceMatchId) {
            return;
        }
        setIsBookDifferenceSubmitting(true);
        try {
            await api.Reconciliation__markMatchManuallyCompleted({
                match_id: bookDifferenceMatchId,
                difference_booking_date: values.difference_booking_date,
                difference_booking_reason: values.difference_booking_reason,
                difference_booking_gla_id: values.difference_booking_gla_id,
            });
            setBookDifferenceMatchId(null);
            await onLoad(startDate, quarterYear, clientScope.selectedClientId, endDate);
        } catch (error) {
            alert('Er is iets misgegaan tijdens het wegboeken van het verschil');
            throw error;
        } finally {
            setIsBookDifferenceSubmitting(false);
        }
    };

    function handleWrongGLAClick(dnd: DndTransaction) {
        setTransactionToBook({
            id: dnd.transaction_id,
            generalLedgerAccountId: dnd.general_ledger_account_id,
            relationName: dnd.relation_name ?? "",
            relationIBAN: dnd.relation_iban ?? "",
        });
    }

    function handleBookAwayClick(dnd: DndTransaction) {
        setTransactionToBookAway({
            id: dnd.transaction_id,
            generalLedgerAccountId: dnd.general_ledger_account_id,
            relationName: dnd.relation_name ?? "",
            relationIBAN: dnd.relation_iban ?? "",
            signedAmount: dnd.total_amount_payable.signed_amount,
        });
    }

    async function handleBookAwaySubmit(payload: BookAwayPayload) {
        setIsRecategorizationModalLoading(true);
        try {
            await api.CategorizationEngine__excludeTransactionFromMatching(payload);
            setTransactionToBookAway(null);
            await onLoad(startDate, quarterYear, clientScope.selectedClientId, endDate);
            dispatch({
                title: 'Transactie weggeboekt',
                description: 'De transactie is succesvol weggeboekt.',
                type: 'success',
            });
        } catch (err) {
            console.error("Error booking transaction:", err);
            setTransactionToBookAway(null);
            dispatch({
                title: 'Fout bij wegboeken',
                description: 'Er is een fout opgetreden. Probeer het later opnieuw.',
                type: 'error',
            });
        } finally {
            setIsRecategorizationModalLoading(false);
        }
    }

    async function handleBookTransactionSubmit(
        transaction: TransactionToBook,
        glAccountToBook: GlAccount,
        feedback: string | null,
        subledgerId: string | null
    ) {
        setIsRecategorizationModalLoading(true);
        try {
            const isDefaultGlAccount = glAccountToBook.id === DEFAULT_GL_ACCOUNT.id;
            await api.CategorizationEngine__excludeTransactionFromMatching({
                general_ledger_account_id: isDefaultGlAccount ? null : glAccountToBook.id,
                feedback: feedback ?? '',
                transaction_id: transaction.id,
                vat_code: null,
                direction: null,
                subledger_id: subledgerId,
            });
            setTransactionToBook(null);
            await onLoad(startDate, quarterYear, clientScope.selectedClientId, endDate);
            dispatch({
                title: 'Transactie hercategoriseerd',
                description: 'De transactie(s) zijn succesvol hercategoriseerd.',
                type: 'success',
            });
        } catch (err) {
            console.error("Error recategorizing transaction:", err);
            setTransactionToBook(null);
            dispatch({
                title: 'Fout bij hercategoriseren',
                description: 'Er is een fout opgetreden. Probeer het later opnieuw.',
                type: 'error',
            });
        } finally {
            setIsRecategorizationModalLoading(false);
        }
    }

    const handleOpenCreateBlacklistRule = (
        transaction: MissingDocumentRequest,
        client: Client | null
    ) => {
        const initialValues = {
            businessId: client?.id || null,
            relationName: transaction.relation_name || null,
            relationIban: transaction.relation_iban || null,
            amount: transaction.total_amount_payable?.signed_amount !== undefined
                ? transaction.total_amount_payable.signed_amount / 100
                : null,
            description: transaction.desc || null,
            generalLedgerAccountId: transaction.general_ledger_account_id ?? null,
        };

        const {selectedBusiness} = buildInitialBlacklistRuleState(
            clients,
            initialValues.businessId,
            initialValues.relationName,
            initialValues.relationIban,
            initialValues.amount,
            initialValues.description
        );

        setBlacklistRuleDrawerInitialValues(initialValues);
        setBlacklistRuleDrawerSelectedBusiness(selectedBusiness);
        setIsCreateBlacklistRuleDrawerOpen(true);
    };

    const handleOpenCreditCardSettlement = async (
        transaction: MissingDocumentRequest,
        businessId: string | null,
    ) => {
        setCreditCardSettlementTransaction(transaction);
        setCreditCardSettlementBusinessId(businessId);
        setCreditCardSettlementDocuments([]);
        setCreditCardSettlementAccounts([]);
        setCreditCardSettlementPreviewDocument(null);
        setIsCreditCardSettlementDrawerOpen(true);

        if (!businessId) {
            return;
        }

        setIsCreditCardSettlementLoading(true);
        try {
            const [documentsResponse, bankAccountsResponse] = await Promise.all([
                api.Partner__listCreditCardStatements(
                    null, null, businessId,
                ),
                api.Partner__entrepreneur__getBankAccounts(businessId),
            ]);

            const unsettledCreditCardStatements = documentsResponse.credit_card_statements.filter(
                (document) => !document.is_credit_card_statement_settled,
            );
            setCreditCardSettlementDocuments(mapCreditCardStatements(unsettledCreditCardStatements));
            setCreditCardSettlementAccounts(
                bankAccountsResponse
                    .filter((account) => account.account_type === BankAccountType.CREDIT_CARD)
                    .map((account) => CustomerBankAccount.fromApiResponse({
                        ...account,
                        business_id: businessId,
                    } as BankAccountData)),
            );
        } catch (error) {
            console.error("Error loading credit card settlement data:", error);
            alert("Er is iets misgegaan tijdens het laden van creditcard gegevens");
        } finally {
            setIsCreditCardSettlementLoading(false);
        }
    };

    const creditCardSettlementPreviewSeqRef = useRef(0);

    const handleCreditCardSettlementSelectDocument = async (documentId: string) => {
        if (!creditCardSettlementBusinessId) {
            return;
        }

        const seq = ++creditCardSettlementPreviewSeqRef.current;
        setIsCreditCardSettlementPreviewLoading(true);
        try {
            const [response, blob] = await Promise.all([
                api.Partner__client__getDocument(
                    creditCardSettlementBusinessId,
                    documentId,
                ),
                fetchDocumentContentBlob(api, 'partner/client/get-document-content', {
                    business_id: creditCardSettlementBusinessId,
                    document_id: documentId,
                }),
            ]);
            if (seq !== creditCardSettlementPreviewSeqRef.current) return;
            setCreditCardSettlementPreviewDocument({ ...(response as unknown as TFICDocument), file_blob: blob });
        } catch (error) {
            console.error("Error loading credit card statement preview:", error);
            if (seq !== creditCardSettlementPreviewSeqRef.current) return;
            setCreditCardSettlementPreviewDocument(null);
            alert("Er is iets misgegaan tijdens het laden van het afschrift");
        } finally {
            if (seq === creditCardSettlementPreviewSeqRef.current) {
                setIsCreditCardSettlementPreviewLoading(false);
            }
        }
    };

    const handleCreditCardSettlementSubmit = async (payload: CreditCardSettlementFormPayload) => {
        if (!creditCardSettlementBusinessId || !creditCardSettlementTransaction) {
            return;
        }

        setIsCreditCardSettlementSubmitting(true);
        try {
            await api.Partner__processCreditCardSettlement(
                buildSettlementSubmitPayload(
                    creditCardSettlementBusinessId,
                    creditCardSettlementTransaction,
                    payload,
                ),
            );
            dispatch({
                title: 'Creditcard afschrift verwerkt',
                description: 'De onderliggende transacties zijn toegevoegd.',
                type: 'success',
            });
            setIsCreditCardSettlementDrawerOpen(false);
            setCreditCardSettlementTransaction(null);
            setCreditCardSettlementBusinessId(null);
            setCreditCardSettlementDocuments([]);
            setCreditCardSettlementAccounts([]);
            setCreditCardSettlementPreviewDocument(null);
            await onLoad(startDate, quarterYear, clientScope.selectedClientId, endDate);
        } catch (error) {
            alert("Er is iets misgegaan tijdens het verwerken van het creditcard afschrift");
        } finally {
            setIsCreditCardSettlementSubmitting(false);
        }
    };

    const handlePreviewBlacklistRule = async (
        rules: Rule[],
        clientBusinessId: string | null | undefined
    ) => {
        try {
            const previewTransactionsResponse = await api.Partner__getBlacklistRulePreview({
                rules: rules,
                client_business_id: clientBusinessId
            });
            return previewTransactionsResponse;
        } catch (error) {
            alert('Er is een fout opgetreden, het is niet mogelijk om een ​​voorbeeld te bekijken.');
            throw error;
        }
    };

    const handleCreateBlacklistRule = async (rules: Rule[], glAccount:  GlAccount | null, vatCode: VatCode | null, vatDirection: DocumentDirection | null) => {
        try {
            await api.Partner__unmatchedTransactions__createBacklistRule({
                rules: rules,
                general_ledger_account_id: glAccount?.id || null,
                vat_code: vatCode,
                vat_direction: vatDirection
            });
            setIsCreateBlacklistRuleDrawerOpen(false);
            setBlacklistRuleDrawerSelectedBusiness(null);
            await onLoad(startDate, quarterYear, clientScope.selectedClientId, endDate);
        } catch (e) {
            throw new Error(extractApiErrorMessage(e)
                ?? "Er is een fout opgetreden, het is niet mogelijk om een ​​automatiseringsregel aan te maken.");
        }
    };

    const getFilteredAndSortedData = () => {
        const reconciliationOverview = businessReconciliationOverviews[0];

        const businessTransactions = (reconciliationOverview?.transaction_side || [])
            .filter(t => showMatched || t.render_type !== RenderTypeEnum.TRANSACTION_MATCH_PART);

        const businessDocuments = (reconciliationOverview?.document_side || [])
            .filter(d => showMatched || d.render_type !== RenderTypeEnum.BILL_MATCH_PART);

        const filteredTransactions = filterTransactionsByDirection(businessTransactions, selectedFilter);
        const filteredDocuments = filterDocumentsByDirection(businessDocuments, selectedFilter);

        const sortedTransactions = sortItemsByOption(filteredTransactions, selectedSort);
        const sortedDocuments = sortItemsByOption(filteredDocuments, selectedSort);

        return {
            transactions: sortedTransactions,
            documents: sortedDocuments,
        };
    };

    useEffect(() => {
            onFilterChange(startDate, quarterYear, clientScope.selectedClientId, endDate);
    }, [state, clientScope.selectedClientId]);

    useReloadOnDocumentDrawerChange(() => {
        if (clientScope.selectedClientId) {
            void onLoad(startDate, quarterYear, clientScope.selectedClientId, endDate);
        }
    });

    const businessOverview = businessReconciliationOverviews[0] ?? null;
    const { transactions, documents } = getFilteredAndSortedData();
    const selectedClient = clientScope.selectedClient;

    return (
        <SubledgerProvider user={currentUser} clientBusinessId={clientScope.selectedClientId || null}>
        <Shell currentUser={currentUser} title="Vraagposten" subtitle={<ClientSubtitle prefix="Vraagposten van" clientName={selectedClient?.name ?? null} fallback="Document aanvragen voor je klanten"/>} titleActions={
            <div className="flex items-center gap-x-4">
                <div className="flex items-center gap-x-2">
                    <span className="text-sm">Ook gematched weergeven</span>
                    <Switch
                        checked={showMatched}
                        onChange={changeShowMatched}
                        className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 data-[checked]:bg-primary"
                    >
                            <span
                                aria-hidden="true"
                                className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
                            />
                    </Switch>
                </div>
                <PartnerClientToolbar
                    clients={clientScope.clients}
                    selectedClientId={clientScope.selectedClientId}
                    recentClientIds={clientScope.recentClientIds}
                    goToItemIds={getVisibleGoToItemIds((flag) => currentUser.flags.getFlag(flag))}
                    onSelectClient={clientScope.selectClient}
                />
            </div>
        }>

            <div className="relative z-20 mb-4">
                <UnmatchedTransactionsFilterBar
                    selectedSort={selectedSort}
                    onSortChange={handleSortChange}
                    selectedFilter={selectedFilter}
                    onFilterChange={handleFilterChange}
                />
            </div>

            <div className="flex flex-col gap-y-4">
                {!clientScope.selectedClientId ? (
                    <div className="flex flex-col items-center justify-center pt-24 text-gray-500">
                        <p className="text-base">Selecteer een klant om te beginnen</p>
                        <p className="mt-1 text-sm">Gebruik de klantkiezer rechtsboven.</p>
                    </div>
                ) : (
                    <RelativeLoadingOverlayFrame isLoading={isLoading}>
                        {businessOverview === null && !isLoading && (
                            <span className="text-gray-500 text-center pt-12">Geen openstaande vraagposten gevonden. Pas de filters aan om meer vraagposten te zien.</span>
                        )}
                        {businessOverview !== null && (
                            currentUser.flags.getFlag('PARTNER__CAN_MATCH_DOCUMENTS') ? (
                                <DndMatchPage
                                    key={businessOverview.business_id}
                                    currentUser={currentUser}
                                    initialTransactions={transactions}
                                    initialDocuments={documents}
                                    onAccept={handleAccept}
                                    onAddParts={handleManualAddPartsMatch}
                                    onAddPartsToExistingMatch={handleManualAddPartsMatch}
                                    client={selectedClient}
                                    onBookTransactionAsPrivate={onBookTransactionAsPrivate}
                                    onCreateBlacklistRule={handleOpenCreateBlacklistRule}
                                    onWrongGLAClick={canBookVatOnTransaction ? handleBookAwayClick : handleWrongGLAClick}
                                    onCreditCardSettlement={handleOpenCreditCardSettlement}
                                    onMarkBillAsPrivatelyPaid={onMarkBillAsPrivatelyPaid}
                                    onUnmatch={handleUnmatch}
                                    onMarkAsComplete={handleMarkAsComplete}
                                    showMatched={showMatched}
                                />
                            ) : (
                                <>Je hebt geen toestemming om documenten te matchen</>
                            )
                        )}
                    </RelativeLoadingOverlayFrame>
                )}
            </div>

            <BookTransactionModal
                transactionToBook={transactionToBook}
                isOpen={!!transactionToBook}
                onClose={() => setTransactionToBook(null)}
                onSubmit={handleBookTransactionSubmit}
                isLoading={isRecategorizationModalLoading}
                glAccounts={glAccounts}
                glAccountsLoading={ glAccountsLoading }
            />

            <BookAwayModal
                transactionToBookAway={transactionToBookAway}
                isOpen={!!transactionToBookAway}
                onClose={() => setTransactionToBookAway(null)}
                onSubmit={handleBookAwaySubmit}
                isLoading={isRecategorizationModalLoading}
                glAccounts={glAccounts}
                glAccountsLoading={ glAccountsLoading }
            />

            <BookDifferenceModal
                isOpen={!!bookDifferenceMatchId}
                defaultDate={bookDifferenceDefaultDate}
                isSubmitting={isBookDifferenceSubmitting}
                glAccounts={glAccounts}
                onClose={() => setBookDifferenceMatchId(null)}
                onConfirm={handleConfirmBookDifference}
            />

            <CreateBlacklistRuleDrawer
                isOpen={isCreateBlacklistRuleDrawerOpen}
                onClose={() => {
                    setIsCreateBlacklistRuleDrawerOpen(false);
                    setBlacklistRuleDrawerSelectedBusiness(null);
                    onLoad(startDate, quarterYear, clientScope.selectedClientId, endDate);
                }}
                selectedBusiness={blacklistRuleDrawerSelectedBusiness}
                onSelectedBusinessChange={setBlacklistRuleDrawerSelectedBusiness}
                initialBusinessId={blacklistRuleDrawerInitialValues.businessId}
                initialRelationName={blacklistRuleDrawerInitialValues.relationName}
                initialRelationIban={blacklistRuleDrawerInitialValues.relationIban}
                initialAmount={blacklistRuleDrawerInitialValues.amount}
                initialDescription={blacklistRuleDrawerInitialValues.description}
                initialGeneralLedgerAccountId={blacklistRuleDrawerInitialValues.generalLedgerAccountId}
                clients={clients}
                glAccounts={glAccounts}
                glAccountsLoading={ glAccountsLoading }
                onPreview={handlePreviewBlacklistRule}
                onCreateRule={handleCreateBlacklistRule}
            />

            <CreditCardSettlementDrawer
                isOpen={isCreditCardSettlementDrawerOpen}
                onClose={() => {
                    setIsCreditCardSettlementDrawerOpen(false);
                    setCreditCardSettlementTransaction(null);
                    setCreditCardSettlementBusinessId(null);
                    setCreditCardSettlementDocuments([]);
                    setCreditCardSettlementAccounts([]);
                    setCreditCardSettlementPreviewDocument(null);
                }}
                settlementTransaction={creditCardSettlementTransaction}
                documents={creditCardSettlementDocuments}
                creditCardAccounts={creditCardSettlementAccounts}
                isLoading={isCreditCardSettlementLoading}
                previewDocument={creditCardSettlementPreviewDocument}
                isPreviewLoading={isCreditCardSettlementPreviewLoading}
                isSubmitting={isCreditCardSettlementSubmitting}
                onSelectDocument={handleCreditCardSettlementSelectDocument}
                onSubmit={handleCreditCardSettlementSubmit}
            />
        </Shell>
        </SubledgerProvider>
    );
}
